const PERSIAN_DIGITS = ["۰", "۱", "۲", "۳", "۴", "۵", "۶", "۷", "۸", "۹"];

// Maps both Western (0-9) and Persian (۰-۹) digit characters to Persian —
// character-substitution rather than a numeric round-trip, so it's safe
// to call on a string that might already contain Persian digits (e.g. an
// admin-typed footnote marker) without corrupting it.
const DIGIT_MAP = PERSIAN_DIGITS.reduce((map, persianDigit, index) => {
  map[index] = persianDigit;
  map[persianDigit] = persianDigit;
  return map;
}, {});

// Converts 1, 2, 3, ... to the zero-padded Persian-numeral ordinals used
// for card indices across the site ("۰۱", "۰۲", ...).
export function toPersianOrdinal(n) {
  return toPersianDigits(String(n).padStart(2, "0"));
}

// Converts any Western/Persian digits in a value (a plain count like
// issues.length, or a string that may already contain Persian digits) to
// Persian numerals — used for the "۱۲ شماره" style label on نشریه‌ها and
// for footnote reference/list numbers on content pages.
export function toPersianDigits(value) {
  return String(value).replace(/[0-9۰-۹]/g, (digit) => DIGIT_MAP[digit]);
}

const PERSIAN_ARABIC_BLOCK = /[؀-ۿ]/;

// Same substitution, but for admin-authored HTML (article/topic bodies):
// walks every text node so any number typed into the prose itself — a
// year, a page reference, a statistic — renders in Persian numerals too,
// not just the structural bits (footnote markers, list numbering) the
// site generates itself. Never touches attributes (so URLs/src paths are
// untouched), only visible text.
//
// Skips a text node instead of converting it when either is true:
//   - it sits inside a block the admin explicitly marked dir="ltr" (the
//     rich-text editor's LTR toggle — see RichTextEditor.js), or
//   - the node itself has no Persian/Arabic character at all, i.e. it
//     reads as a run of English text (a whole English footnote, a title
//     copy-pasted in English, ...).
// Numbers inside those stay as whatever digits were actually typed
// (normally Western) instead of being forced to Persian numerals, which
// would look wrong sitting in the middle of English prose.
export function persianizeDigitsInHtml(html) {
  if (!html || !/[0-9]/.test(html)) return html;

  const doc = new DOMParser().parseFromString(html, "text/html");
  const walker = doc.createTreeWalker(doc.body, NodeFilter.SHOW_TEXT);
  let node = walker.nextNode();
  while (node) {
    const text = node.textContent;
    const isExplicitlyLtr = node.parentElement && node.parentElement.closest('[dir="ltr"]');
    const isEnglishRun = !PERSIAN_ARABIC_BLOCK.test(text);
    if (/[0-9]/.test(text) && !isExplicitlyLtr && !isEnglishRun) {
      node.textContent = toPersianDigits(text);
    }
    node = walker.nextNode();
  }

  return doc.body.innerHTML;
}
